import { useMemo, useState } from 'react'
import { formatCurrency, pricing } from '../data/pricing'
import { serviceAreas, type ServiceAreaId } from '../data/serviceAreas'
import { services, type ServiceId } from '../data/services'
import {
  calculateQuote,
  type ApostillePackage,
  type FieldInspectionType,
} from '../lib/quote'

const inspectionLabels: Record<FieldInspectionType, string> = {
  occupancyVerification: 'Occupancy verification',
  insuranceAudit: 'Insurance audit',
  commercialInspection: 'Commercial inspection',
  delinquencyInterview: 'Delinquency interview',
  leasedEquipmentInspection: 'Leased equipment inspection',
}

export function QuoteEstimator() {
  const [serviceId, setServiceId] = useState<ServiceId>('mobile-notary')
  const [areaId, setAreaId] = useState<ServiceAreaId>('bentonville')
  const [signers, setSigners] = useState(1)
  const [stamps, setStamps] = useState(1)
  const [documents, setDocuments] = useState(1)
  const [courierNeeded, setCourierNeeded] = useState(false)
  const [inspectionType, setInspectionType] = useState<FieldInspectionType>('occupancyVerification')
  const [apostillePackage, setApostillePackage] = useState<ApostillePackage>('full')
  const [hasCalculated, setHasCalculated] = useState(false)

  const result = useMemo(
    () =>
      calculateQuote({
        serviceId,
        areaId,
        signers,
        stamps,
        documents,
        courierNeeded,
        fieldInspectionType: inspectionType,
        apostillePackage,
      }),
    [apostillePackage, areaId, courierNeeded, documents, inspectionType, serviceId, signers, stamps],
  )

  const showSignerFields = serviceId === 'mobile-notary' || serviceId === 'e-notary'
  const showDocuments = serviceId === 'certified-copies' || serviceId === 'apostille-assistance'

  return (
    <section className="estimator" aria-labelledby="estimator-title">
      <div className="estimator-heading">
        <span className="eyebrow">Plan with clarity</span>
        <h2 id="estimator-title">Preliminary quote estimator</h2>
        <p>Use the known pricing below as a starting point. Complex routes and assignments require a custom quote.</p>
      </div>
      <form
        className="estimator-grid"
        onSubmit={(event) => {
          event.preventDefault()
          setHasCalculated(true)
        }}
      >
        <label>
          Service
          <select value={serviceId} onChange={(event) => setServiceId(event.target.value as ServiceId)}>
            {services.map((service) => (
              <option key={service.id} value={service.id}>{service.shortName}</option>
            ))}
          </select>
        </label>
        <label>
          City or service area
          <select value={areaId} onChange={(event) => setAreaId(event.target.value as ServiceAreaId)}>
            {serviceAreas.map((area) => (
              <option key={area.id} value={area.id}>{area.name} — {area.displayPrice} travel</option>
            ))}
          </select>
        </label>

        {showSignerFields && (
          <>
            <label>
              Number of signers
              <input type="number" min="1" max="20" value={signers} onChange={(event) => setSigners(Number(event.target.value))} />
            </label>
            <label>
              Number of notarizations / stamps
              <input type="number" min="1" max="100" value={stamps} onChange={(event) => setStamps(Number(event.target.value))} />
            </label>
          </>
        )}

        {showDocuments && (
          <label>
            Number of documents
            <input type="number" min="1" max="100" value={documents} onChange={(event) => setDocuments(Number(event.target.value))} />
          </label>
        )}

        {serviceId === 'field-inspections' && (
          <label>
            Inspection type
            <select value={inspectionType} onChange={(event) => setInspectionType(event.target.value as FieldInspectionType)}>
              {Object.entries(inspectionLabels).map(([id, label]) => (
                <option key={id} value={id}>{label} — {formatCurrency(pricing.fieldInspections[id as FieldInspectionType])}</option>
              ))}
            </select>
          </label>
        )}

        {serviceId === 'apostille-assistance' && (
          <label>
            Assistance package
            <select value={apostillePackage} onChange={(event) => setApostillePackage(event.target.value as ApostillePackage)}>
              <option value="full">Full handling package</option>
              <option value="limited">Limited per-document assistance</option>
            </select>
          </label>
        )}

        <label className="check-label">
          <input type="checkbox" checked={courierNeeded} onChange={(event) => setCourierNeeded(event.target.checked)} />
          Courier coordination needed
        </label>

        <button className="button" type="submit">Calculate estimate</button>
      </form>

      <div className="estimate-result" aria-live="polite" aria-atomic="true">
        {!hasCalculated ? (
          <p>Select your service details, then calculate a preliminary estimate.</p>
        ) : result.status === 'estimate' ? (
          <>
            <span className="estimate-label">Preliminary estimate</span>
            <strong>{formatCurrency(result.total ?? 0)}</strong>
            <dl>
              <div><dt>Service</dt><dd>{formatCurrency(result.serviceSubtotal ?? 0)}</dd></div>
              <div><dt>Travel</dt><dd>{formatCurrency(result.travelFee ?? 0)}</dd></div>
            </dl>
            <p>{result.message}</p>
          </>
        ) : (
          <>
            <span className="estimate-label">Quote required</span>
            <strong>Let’s confirm the details</strong>
            <p>{result.message}</p>
          </>
        )}
        <p className="fine-print">This is an estimate, not a confirmed price or appointment. Final service and travel fees will be confirmed before the appointment is scheduled.</p>
      </div>
    </section>
  )
}
